import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, finalize, of } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { TreeNode } from 'primeng/api';

import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import type {
  FormFieldConfig,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { BusinessUnitTreeNode } from '@app/api/user-service/model/businessUnitTreeNode';
import { TreeModule } from 'primeng/tree';
import { EmployeeMgmtService } from '../../services/employee-mgmt.service';

export interface AssignRolesDialogData {
  selectedUserIds: string[];
}

export interface AssignRolesDialogResult {
  roleNames?: string[];
  businessUnitIds?: number[];
}

const ASSIGNABLE_BU_TYPES = ['STORE', 'WAREHOUSE'];

export interface RoleRow {
  id?: string;
  name?: string;
  description?: string;
}

const ROLES_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'search',
    label: 'configuration.user_roles_assignment.dialog.role_search',
    type: 'text',
    placeholder: 'configuration.user_roles_assignment.dialog.role_search_ph',
    width: '85%',
  },
];

const ROLES_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'configuration.user_roles_assignment.dialog.role_name',
    prop: 'name',
    width: 240,
  },
  {
    label: 'configuration.user_roles_assignment.dialog.role_description',
    prop: 'description',
    width: 420,
    styleClass: 'description-cell-wrap',
  },
];

@Component({
  selector: 'app-assign-roles-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SearchFormComponent,
    DataTableComponent,
    AppButtonComponent,
    TreeModule,
  ],
  templateUrl: './assign-roles-dialog.component.html',
  styleUrl: './assign-roles-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignRolesDialogComponent {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userRolesAssignmentService = inject(EmployeeMgmtService);

  readonly activeTab = signal<'roles' | 'bu'>('roles');
  readonly searchFormConfig = ROLES_SEARCH_CONFIG;
  readonly columns = ROLES_TABLE_COLUMNS;
  readonly roles = signal<RoleRow[]>([]);
  readonly loading = signal(false);
  readonly totalRecords = signal(0);
  readonly selectedRoles = signal<RoleRow[]>([]);
  readonly first = signal(0);
  readonly pageSize = 20;
  readonly searchParams = signal({ search: '', page: 0, size: 20 });
  private readonly searchTrigger$ = new Subject<void>();

  readonly buTreeNodes = signal<TreeNode[]>([]);
  readonly buLoading = signal(false);
  /** Single selected BU node (one BU for many users). */
  selectedBuNode: TreeNode | null = null;

  constructor() {
    this.setupSearchPipeline();
    this.searchTrigger$.next();
    this.loadBusinessUnitTree();
  }

  setTab(tab: 'roles' | 'bu'): void {
    this.activeTab.set(tab);
  }

  private loadBusinessUnitTree(): void {
    this.buLoading.set(true);
    this.userRolesAssignmentService
      .getBusinessUnitTree()
      .pipe(
        catchError(() => of([] as BusinessUnitTreeNode[])),
        finalize(() => this.buLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((nodes) => {
        this.buTreeNodes.set(this.mapToTreeNodes(nodes || []));
      });
  }

  private mapToTreeNodes(units: BusinessUnitTreeNode[]): TreeNode[] {
    return units.map((unit) => this.unitToTreeNode(unit));
  }

  private unitToTreeNode(unit: BusinessUnitTreeNode): TreeNode {
    const raw = unit as Record<string, unknown>;
    const childKeys = ['children', 'subUnits', 'businessUnits'];
    let children: BusinessUnitTreeNode[] = [];
    for (const key of childKeys) {
      const c = raw[key];
      if (Array.isArray(c)) {
        children = c as BusinessUnitTreeNode[];
        break;
      }
    }
    const childNodes = children.map((c) => this.unitToTreeNode(c));
    return {
      key: String(unit.id ?? unit.code ?? unit.name ?? ''),
      label: unit.name || unit.code || '',
      data: unit,
      children: childNodes.length > 0 ? childNodes : undefined,
    };
  }

  getSelectedBusinessUnitIds(): number[] {
    const node = this.selectedBuNode;
    if (!node?.data) return [];
    const data = node.data as BusinessUnitTreeNode;
    if (
      data.id == null ||
      !data.type ||
      !ASSIGNABLE_BU_TYPES.includes(String(data.type))
    ) {
      return [];
    }
    return [Number(data.id)];
  }

  canConfirm(): boolean {
    return (
      this.selectedRoles().length > 0 ||
      this.getSelectedBusinessUnitIds().length > 0
    );
  }

  private setupSearchPipeline(): void {
    this.searchTrigger$
      .pipe(
        debounceTime(150),
        switchMap(() => {
          this.loading.set(true);
          const params = this.searchParams();
          return this.userRolesAssignmentService.getRolesPage(
            params.page,
            params.size,
            params.search || undefined,
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((page) => {
        const rows = page.pageContent.map((r: Record<string, unknown>) => ({
          id: r['id'],
          name: r['name'],
          description: r['description'],
        })) as RoleRow[];
        this.roles.set(rows);
        this.totalRecords.set(page.totalElements);
        this.first.set(this.searchParams().page * this.searchParams().size);
        this.loading.set(false);
      });
  }

  onSearch(formData: SearchFormData): void {
    this.first.set(0);
    this.searchParams.update((p) => ({
      ...p,
      search: (formData['search'] ?? '').toString().trim(),
      page: 0,
    }));
    this.searchTrigger$.next();
  }

  onReset(): void {
    this.first.set(0);
    this.searchParams.update((p) => ({ ...p, search: '', page: 0 }));
    this.searchTrigger$.next();
  }

  onPageChange(event: { first?: number; rows?: number }): void {
    const rows = event.rows ?? this.searchParams().size;
    const page =
      typeof event.first === 'number' ? Math.floor(event.first / rows) : 0;
    this.searchParams.update((p) => ({ ...p, page, size: rows }));
    this.first.set(page * rows);
    this.searchTrigger$.next();
  }

  onSelectionChange(selection: RoleRow[]): void {
    this.selectedRoles.set(selection);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    const result: AssignRolesDialogResult = {};

    const roles = this.selectedRoles();
    const roleNames = roles
      .map((r) => r?.name)
      .filter((name): name is string => !!name);
    if (roleNames.length > 0) {
      result.roleNames = roleNames;
    }

    const businessUnitIds = this.getSelectedBusinessUnitIds();
    if (businessUnitIds.length > 0) {
      result.businessUnitIds = businessUnitIds;
    }

    this.dialogRef.close(result);
  }
}
